import {Stack} from '@mui/material';
import {Moment} from 'moment';
import {useTranslation} from 'react-i18next';
import GenericHeader from '../../../../../../../components/generic-header/generic-header';
import {translations} from '../../../../../../../i18n/translations';
import {PlannerAddMenuButton} from './planner-add-menu-button';
import {PlannerDisplayMode} from '../shared/planner-display-mode';
import {DateRangeInput} from '../../../../../../../components/date-range-input/date-range-input';
import {PlannerAddSelectionItem} from './planner-add-selection-item';
import {ViewTypeToggler} from '../../../../../../../components/view-type-toggler/view-type-toggler';
import {Menu, CalendarMonth} from '@mui/icons-material';
import {UsersFilters} from '../../../../../../../components/users-filters';
import {
  createUsersFilters,
  UserFilters,
} from '../../../../../../../components/users-filters/users-filters-utils';
import {AffiliatedUser} from '../../../../../../../backend/responses/affiliated-user';
import {FetchActivitiesSectorsAndGroups} from '../shared/use-fetch-activities-sectors-and-groups';
import {RolePermissionType} from '../../../../../../../backend/enums/role-permission-type';
import {useAuth} from '../../../../../../../contexts/auth/context';
import {DateRangeOnChange} from '../../../../../../../components/date-range-input/date-range-utils';
import {FetchEntityUsersResult} from '../../../../../../../hooks/use-fetch-entity-users';
import {PlannerUsersInput} from './planner-users-input';

export type PlannerHeaderProps = {
  startDate: Moment;
  endDate: Moment;
  display: PlannerDisplayMode;
  activitiesSectorsAndGroupsData: FetchActivitiesSectorsAndGroups;
  usersData: FetchEntityUsersResult;
  caregiversData: FetchEntityUsersResult;
  onDateChange: DateRangeOnChange;
  onDisplayChange: (mode: PlannerDisplayMode) => void;
  onAddSelection: (item: PlannerAddSelectionItem) => void;
  onFilterUserChange: (user: AffiliatedUser | undefined) => void;
  onFilterCaregiverChange: (caregiver: AffiliatedUser | undefined) => void;
  onFiltersChange: (filters: UserFilters) => void;
};

export function PlannerHeader(props: PlannerHeaderProps) {
  const {t} = useTranslation();
  const {hasWritePermission} = useAuth();
  const canWrite = hasWritePermission(RolePermissionType.Calendar);

  return (
    <GenericHeader
      isLoading={false}
      pageTitle={t(translations.planner.header.title)}
      leftColumn={
        <Stack gap={2} sx={{display: 'flex', flexDirection: 'row', padding: 0, width: '100%'}}>
          {/* --- USERS FILTERING INPUT --- */}
          <PlannerUsersInput usersData={props.usersData} onChange={props.onFilterUserChange} />
          <PlannerUsersInput
            usersData={props.caregiversData}
            defaultOptionLabel={translations.care_services.header.filter_caregivers_label}
            onChange={props.onFilterCaregiverChange}
          />

          {/* --- FILTER INPUT --- */}
          <UsersFilters
            hideActiveStatus
            hideOccurrences
            hideTaskGroups
            taskStatus={[
              {
                key: 'planned',
                label: t(translations.office.users_filters.sections.taskStatus.options.planned),
              },
              {
                key: 'handled',
                label: t(translations.office.users_filters.sections.taskStatus.options.handled),
              },
              {
                key: 'not_handled',
                label: t(translations.office.users_filters.sections.taskStatus.options.not_handled),
              },
              {
                key: 'done',
                label: t(translations.office.users_filters.sections.taskStatus.options.done),
              },
              {
                key: 'not_done',
                label: t(translations.office.users_filters.sections.taskStatus.options.not_done),
              },
            ]}
            onChange={(rawFilters) => {
              const filters = createUsersFilters(rawFilters);
              props.onFiltersChange(filters);
            }}
          />

          <ViewTypeToggler
            value={props.display}
            options={[
              {
                id: 'calendar',
                icon: CalendarMonth,
                name: t(translations.planner.header.toggler.calendar),
              },
              {
                id: 'agenda',
                icon: Menu,
                name: t(translations.planner.header.toggler.list),
              },
            ]}
            onClick={props.onDisplayChange}
          />
        </Stack>
      }
      rightColumn={
        <Stack direction="row" justifyContent="flex-end" alignItems="center" gap="10px">
          {/* --- DATE INPUT --- */}
          <DateRangeInput
            mode="strict"
            startDate={props.startDate}
            endDate={props.endDate}
            onChange={props.onDateChange}
          />

          {/* --- ADD INPUT --- */}
          {canWrite && (
            <PlannerAddMenuButton
              activitiesSectorsAndGroupsData={props.activitiesSectorsAndGroupsData}
              onSelected={props.onAddSelection}
            />
          )}
        </Stack>
      }
    />
  );
}
